import React from "react";
import { PieChart, Pie, Cell, Label } from "recharts";
import { ChevronDown } from "lucide-react";
import { Box, Grid, Typography, Select, MenuItem } from "@mui/material";
import backgroundImage from "../../assets/images/Group 1171276166.svg";

interface DonutChartCardProps {
  title: string;
  chartTitle?: string;
  data: { name: string; value: number }[];
  total?: number;
  dropdownOptions?: string[];
  selectedOption?: string;
  colors?:string[];
  onOptionChange?: (value: string) => void;
}

const COLORS = ["#104262", "#007BFF", "#5AB8F3", "#9BD0FF"];

const ProgressChartCard: React.FC<DonutChartCardProps> = ({
  title,
  chartTitle,
  data,
  total,
  dropdownOptions = [],
  selectedOption,
  colors,
  onOptionChange,
}) => {
  // Check if data is empty or all values are 0
  const hasData =
    data && data.length > 0 && data.some((item) => item.value > 0);
  const displayTotal = total || 0;

   const chartColors = colors && colors.length > 0 ? colors : COLORS;
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        borderRadius: 2,
        p: 2,
        width: "100%",
        height:"100%",
        border: "1px solid #D6E6FB",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left Background Image */}
      <Box
        component="img"
        src={backgroundImage}
        alt="background"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "130%",
          width: "70%",
          zIndex: 0,
        }}
      />

      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, position: "relative", zIndex: 1 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#017CC9", fontWeight: 600, fontSize: "16px" }}
        >
          {title}
        </Typography>

        {dropdownOptions.length > 0 && (
          <Box sx={{ position: "relative" }}>
            <Select
              value={selectedOption}
              onChange={(e) => onOptionChange?.(e.target.value)}
              sx={{
                backgroundColor: "#017CC9",
                color: "#fff",
                fontSize: 13,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                "& .MuiSelect-icon": { color: "#fff" },
              }}
            >
              {dropdownOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Grid>

      {hasData ? (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          sx={{
            position: "relative",
            zIndex: 1,
            flexDirection: { xs: "column", md: "row" }, // Column on mobile, row on desktop
            gap: { xs: 2, md: "50px" },
          }}
        >
          {/* Donut Chart */}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <PieChart width={160} height={160}>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  paddingAngle={1}
                  startAngle={360}
                  endAngle={-90}
                >
                  {data.map((entry, index) => (
                   <Cell key={index} fill={chartColors[index % chartColors.length]} />

                  ))}
                  <Label
                    position="center"
                    content={() => (
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x="50%"
                          dy="-6"
                          style={{ fontSize: "14px" }}
                          fill="#575757"
                        >
                          Total
                        </tspan>
                        <tspan
                          x="50%"
                          dy="19"
                          style={{ fontSize: "14px" }}
                          fill="#575757"
                        >
                          {chartTitle}
                        </tspan>
                        <tspan
                          x="50%"
                          dy="18"
                          style={{ fontSize: "16px", fontWeight: "bold" }}
                          fill="#575757"
                        >
                          {displayTotal}
                        </tspan>
                      </text>
                    )}
                  />
                </Pie>
              </PieChart>
            </Box>
          </Grid>

          {/* Legend */}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "center" }, // left on mobile
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                pl: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
              }}
            >
              {data.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: chartColors[index % chartColors.length],

                    }}
                  />
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 400, color: "#575757" }}
                  >
                    {item.name}:{" "}
                    <Box
                      component="span"
                      sx={{ fontSize: 14, color: "#575757" }}
                    >
                      {item.value}
                    </Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "150px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              color: "#9E9E9E",
              textAlign: "center",
            }}
          >
            No data found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProgressChartCard;
